<template>
  <div class="employee-container">
    <div class="page-header">
      <h2>员工管理</h2>
      <p>管理公司员工信息</p>
    </div>
    
    <div class="content-area">
      <el-card>
        <template #header>
          <div class="card-header">
            <span>员工列表</span>
            <div class="header-actions">
              <el-button type="primary" size="small" @click="handleAdd">添加员工</el-button>
              <el-button type="success" size="small" @click="handleExport">批量导入</el-button>
            </div>
          </div>
        </template>
        
        <!-- 搜索区域 -->
        <div class="search-area">
          <el-form :inline="true" :model="searchForm" class="demo-form-inline">
            <el-form-item label="姓名">
              <el-input v-model="searchForm.name" placeholder="请输入员工姓名" clearable />
            </el-form-item>
            <el-form-item label="手机号">
              <el-input v-model="searchForm.phone" placeholder="请输入手机号" clearable />
            </el-form-item>
            <el-form-item label="部门">
              <el-select v-model="searchForm.department" placeholder="请选择部门" style="width: 120px;" clearable>
                <el-option
                  v-for="dept in departmentList"
                  :key="dept.id"
                  :label="dept.department"
                  :value="dept.id" />
              </el-select>
            </el-form-item>
            <el-form-item>
              <el-button type="primary" @click="handleSearch">查询</el-button>
              <el-button @click="handleReset">重置</el-button>
            </el-form-item>
          </el-form>
        </div>

        <!-- 员工列表 -->
        <el-table
          :data="employeeList"
          v-loading="loading"
          style="width: 100%"
          @selection-change="handleSelectionChange">
          <el-table-column type="selection" width="55" />
          <el-table-column prop="name" label="姓名" width="120" />
          <el-table-column prop="sex" label="性别" width="80">
            <template #default="scope">
              <el-tag :type="scope.row.sex === '0' ? 'primary' : 'success'">
                {{ scope.row.sex === '0' ? '男' : '女' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="phone" label="手机号" width="150" />
          <el-table-column prop="sfz" label="身份证号" width="180" />
          <el-table-column prop="department" label="部门" width="120">
            <template #default="scope">
              {{ getDepartmentName(scope.row.department) }}
            </template>
          </el-table-column>
          <el-table-column prop="remarks" label="备注" show-overflow-tooltip />
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button size="small" @click="handleEdit(scope.row)">编辑</el-button>
              <el-button size="small" type="danger" @click="handleDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-area">
          <el-pagination
            v-model:current-page="pagination.currentPage"
            v-model:page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange" />
        </div>
      </el-card>
    </div>

    <!-- 添加/编辑员工对话框 -->
    <el-dialog
      :title="dialogTitle"
      v-model="dialogVisible"
      width="500px"
      @close="handleDialogClose">
      <el-form
        ref="employeeFormRef"
        :model="employeeForm"
        :rules="employeeRules"
        label-width="80px">
        <el-form-item label="姓名" prop="name">
          <el-input v-model="employeeForm.name" placeholder="请输入员工姓名" />
        </el-form-item>
        <el-form-item label="性别" prop="sex">
          <el-radio-group v-model="employeeForm.sex">
            <el-radio label="0">男</el-radio>
            <el-radio label="1">女</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="employeeForm.phone" placeholder="请输入手机号" />
        </el-form-item>
        <el-form-item label="身份证号" prop="sfz">
          <el-input v-model="employeeForm.sfz" placeholder="请输入身份证号" />
        </el-form-item>
        <el-form-item label="部门" prop="department">
          <el-select v-model="employeeForm.department" placeholder="请选择部门" style="width: 100%">
            <el-option
              v-for="dept in departmentList"
              :key="dept.id"
              :label="dept.department"
              :value="dept.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input
            v-model="employeeForm.remarks"
            type="textarea"
            :rows="3"
            placeholder="请输入备注信息" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting">确定</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 导入Excel对话框 -->
    <el-dialog
      title="导入员工数据"
      v-model="importDialogVisible"
      width="500px"
      @close="handleImportDialogClose">
      <el-form
        ref="importFormRef"
        :model="importForm"
        :rules="importRules"
        label-width="100px">
        <el-form-item label="Excel文件" prop="file">
          <el-upload
            ref="uploadRef"
            class="upload-demo"
            :auto-upload="false"
            :limit="1"
            :on-change="handleFileChange"
            :on-remove="handleFileRemove"
            accept=".xlsx,.xls"
            :file-list="importFile ? [importFile] : []">
            <el-button type="primary">选择文件</el-button>
            <template #tip>
              <div class="el-upload__tip">
                请上传Excel文件，支持 .xlsx 和 .xls 格式
              </div>
            </template>
          </el-upload>
        </el-form-item>
        <el-form-item>
          <el-button type="text" @click="handleDownloadTemplate">
            <el-icon><Download /></el-icon>
            下载导入模板
          </el-button>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="importDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleImportSubmit" :loading="importLoading">导入</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import { Download } from '@element-plus/icons-vue'

export default {
  name: 'EmployeePage',
  data() {
    return {
      loading: false,
      employeeList: [],
      departmentList: [],
      selectedEmployees: [],
      searchForm: {
        name: '',
        phone: '',
        department: ''
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      dialogVisible: false,
      dialogTitle: '添加员工',
      submitting: false,
      employeeForm: {
        id: '',
        name: '',
        sex: '0',
        phone: '',
        sfz: '',
        department: '',
        remarks: ''
      },
      employeeRules: {
        name: [
          { required: true, message: '请输入员工姓名', trigger: 'blur' }
        ],
        sex: [
          { required: true, message: '请选择性别', trigger: 'change' }
        ],
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        sfz: [
          { pattern: /^\d{17}[\dXx]$/, message: '请输入正确的身份证号', trigger: 'blur' }
        ],
        department: [
          { required: true, message: '请选择部门', trigger: 'change' }
        ]
      },
      // 导入相关
      importDialogVisible: false,
      importLoading: false,
      importFile: null,
      importForm: {
        file: null
      },
      importRules: {
        file: [
          { required: true, message: '请选择Excel文件', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    employeeFormRef() {
      return this.$refs.employeeFormRef
    }
  },
  mounted() {
    // 先加载部门列表，再加载员工列表，确保部门数据可用
    this.getDepartmentList().then(() => {
      this.getEmployeeList()
    })
  },
  methods: {
    // 获取部门列表
    async getDepartmentList() {
      try {
        const response = await this.$axios.get('/api/department/getlist')
        if (response.code === 1) {
          this.departmentList = response.data || []
          console.log('部门列表:', this.departmentList)
        } else {
          ElMessage.error(response.msg || '获取部门列表失败')
        }
      } catch (error) {
        console.error('获取部门列表失败:', error)
        ElMessage.error('获取部门列表失败')
      }
    },

    // 获取员工列表
    async getEmployeeList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          pageSize: this.pagination.pageSize,
          ...this.searchForm
        }
        
        // 调用API获取员工列表
        const response = await this.$axios.get('/api/employee/getlist', { params })
        if (response.code === 1) {
          this.employeeList = response.data || []
          this.pagination.total = response.total || 0
        } else {
          ElMessage.error(response.msg || '获取员工列表失败')
        }
      } catch (error) {
        console.error('获取员工列表失败:', error)
        ElMessage.error('获取员工列表失败')
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.currentPage = 1
      this.getEmployeeList()
    },

    // 重置搜索
    handleReset() {
      this.searchForm = {
        name: '',
        phone: '',
        department: ''
      }
      this.pagination.currentPage = 1
      this.getEmployeeList()
    },

    // 添加员工
    handleAdd() {
      this.dialogTitle = '添加员工'
      this.employeeForm = {
        id: '',
        name: '',
        sex: '0',
        phone: '',
        sfz: '',
        department: '',
        remarks: ''
      }
      this.dialogVisible = true
    },

    // 编辑员工
    handleEdit(row) {
      this.dialogTitle = '编辑员工'
      this.employeeForm = { ...row }
      this.dialogVisible = true
    },

    // 删除员工
    async handleDelete(row) {
      try {
        await ElMessageBox.confirm(
          `确定要删除员工 "${row.name}" 吗？`,
          '提示',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )
        
        // 调用删除API
        const response = await this.$axios.post('/api/employee/delete', { id: row.id })
        if (response.code === 1) {
          ElMessage.success('删除成功')
          this.getEmployeeList()
        } else {
          ElMessage.error(response.msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除员工失败:', error)
          ElMessage.error('删除失败')
        }
      }
    },

    // 批量导入
    handleExport() {
      this.importDialogVisible = true
    },

    // 文件选择变化
    handleFileChange(file) {
      this.importFile = file
    },

    // 文件移除
    handleFileRemove() {
      this.importFile = null
    },

    // 导入提交
    async handleImportSubmit() {
      if (!this.importFile) {
        ElMessage.warning('请选择Excel文件')
        return
      }

      this.importLoading = true
      try {
        const formData = new FormData()
        formData.append('file', this.importFile.raw)

        const response = await this.$axios.post('/api/employee/import', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        if (response.code === 1) {
          ElMessage.success('导入成功')
          this.importDialogVisible = false
          this.getEmployeeList()
        } else {
          ElMessage.error(response.msg || '导入失败')
        }
      } catch (error) {
        console.error('导入失败:', error)
        ElMessage.error('导入失败')
      } finally {
        this.importLoading = false
      }
    },

    // 导入对话框关闭
    handleImportDialogClose() {
      this.importFile = null
      this.$refs.uploadRef?.clearFiles()
    },

    // 下载导入模板
    handleDownloadTemplate() {
      // 创建下载链接指向public目录下的模板文件
      const link = document.createElement('a')
      link.href = '/员工导入模板.xlsx'
      link.setAttribute('download', '员工导入模板.xlsx')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      ElMessage.success('模板下载成功')
    },

    // 提交表单
    async handleSubmit() {
      if (!this.employeeFormRef) return

      try {
        await this.employeeFormRef.validate()
        this.submitting = true

        let response
        if (this.employeeForm.id) {
          // 编辑员工
          response = await this.$axios.post('/api/employee/edit', this.employeeForm)
        } else {
          // 添加员工
          response = await this.$axios.post('/api/employee/add', this.employeeForm)
        }
        console.log('提交响应:', response)
        if (response.code === 1) {
          ElMessage.success(this.departmentForm.id ? '编辑成功' : '添加成功')
          this.dialogVisible = false
          this.getEmployeeList()
        } else {
          ElMessage.error(response.msg || '操作失败')
        }
      } catch (error) {
        if (error.response) {
          ElMessage.error(error.response.msg || '操作失败')
        }
      } finally {
        this.submitting = false
      }
    },

    // 对话框关闭
    handleDialogClose() {
      this.employeeFormRef?.resetFields()
    },

    // 选择员工
    handleSelectionChange(selection) {
      this.selectedEmployees = selection
    },

    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.currentPage = 1
      this.getEmployeeList()
    },

    // 当前页改变
    handleCurrentChange(page) {
      this.pagination.currentPage = page
      this.getEmployeeList()
    },

    // 获取部门名称
    getDepartmentName(department) {
      const dept = this.departmentList.find(item => item.id == department)
      return dept ? dept.department : '未知部门'
    }
  }
}
</script>

<style scoped>
.employee-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.page-header p {
  margin: 5px 0 0 0;
  color: #909399;
  font-size: 14px;
}

.content-area {
  margin-top: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.search-area {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.pagination-area {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style>
